import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { CURRENCY_BY_MARKET, PRICE_TABLE, Market } from "./prices";

type PricingState = {
  market: Market;
  format: (amount: number) => string;
  getPrice: (key: keyof (typeof PRICE_TABLE)["DK"]) => number;
};

const PricingCtx = createContext<PricingState | null>(null);

const marketFromLanguage = (language: string | undefined): Market => {
  if (language?.startsWith("da")) return "DK";
  if (language?.startsWith("sv")) return "SE";
  return "INTL";
};

export const PricingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { i18n } = useTranslation();
  const [market, setMarket] = useState<Market>(() =>
    marketFromLanguage(i18n.language)
  );

  // When language changes, choose the closest market for displayed prices.
  useEffect(() => {
    setMarket(marketFromLanguage(i18n.language));
  }, [i18n.language]);

  const { code, locale } = CURRENCY_BY_MARKET[market];

  const format = useMemo(() => {
    const fmt = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      currencyDisplay: "narrowSymbol", // "$" instead of "US$"
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return (amount: number) => fmt.format(amount);
  }, [code, locale]);

  const getPrice = (key: keyof (typeof PRICE_TABLE)["DK"]) =>
    PRICE_TABLE[market][key];

  const value: PricingState = { market, format, getPrice };
  return <PricingCtx.Provider value={value}>{children}</PricingCtx.Provider>;
};

export const usePricing = () => {
  const ctx = useContext(PricingCtx);
  if (!ctx) throw new Error("usePricing must be used within PricingProvider");
  return ctx;
};
